import React, { useMemo } from "react";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";

import { AiNodeQuota, BasicModelCard } from "src/aiNode/types";
import {
  useAiNodeQuotas,
  useAvailableAiModels,
} from "src/api/connectApi/queries";
import { useWorkspace } from "src/api/queries";
import { Card } from "src/base-components/Card";
import { LoadingView } from "src/base-components/LoadingView";
import { Cell, Header, TableComp as Table } from "src/base-components/Table";
import type { ModelProvider } from "src/clients/agent-management";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { SubHeader } from "src/flow/SubHeader";
import { ModelIcon } from "src/genai/ModelSelector/ModelIcon";
import { DashboardContent } from "src/layout/DashboardContent";
import { formatNumber } from "src/utils/numbers";

const PAGE_TOOLTIP =
  "AI usage limits applies collectively to AI Nodes and Agents. Please contact us to discuss increasing your limits.";

const TOKENS_PER_MIN_TOOLTIP =
  "The maximum number of tokens this model can process per minute. Requests exceeding this limit may be rejected.";

const REQUESTS_PER_MIN_TOOLTIP =
  "The maximum number of requests this model can handle per minute. Requests exceeding this limit may be rejected.";

const InfoTooltip: React.FC<{ title: string }> = ({ title }) => (
  <Tooltip placement="right" title={title}>
    <span className="inline-flex text-gray-500">
      <Icon icon={faCircleInfo} size="xs" />
    </span>
  </Tooltip>
);

type Row = {
  modelId: string;
  displayName: string;
  iconPath?: string;
  provider?: string;
  tokensUsed: number;
  tokenQuota: number;
  tokensPerMinuteLimit: number;
  requestsPerMinuteLimit: number;
};

const UNLIMITED_LABEL = "Unlimited";

const isUnlimited = (n: number) => n < 0;

const formatCompactTokens = (n: number) =>
  formatNumber(n, { notation: "compact", maximumFractionDigits: 1 });

const formatLimit = (n: number) =>
  isUnlimited(n) ? UNLIMITED_LABEL : formatCompactTokens(n);

const formatCount = (n: number) =>
  isUnlimited(n) ? UNLIMITED_LABEL : formatNumber(n);

const buildRows = (models: BasicModelCard[], quota: AiNodeQuota): Row[] => {
  const usage = quota.usage ?? {};
  const modelById = new Map(models.map((m) => [m.model_id, m] as const));
  return Object.entries(usage).map(([modelId, u]) => {
    const m = modelById.get(modelId);
    return {
      modelId,
      displayName: m?.name ?? modelId,
      iconPath: m?.icon_path,
      provider: m?.providers?.[0],
      tokensUsed: u.tokens_used ?? 0,
      tokenQuota: u.token_quota,
      tokensPerMinuteLimit: u.tokens_per_minute_limit,
      requestsPerMinuteLimit: u.requests_per_minute_limit,
    };
  });
};

const columnHelper = createColumnHelper<Row>();

const columns = [
  columnHelper.accessor("displayName", {
    header: (info) => <Header info={info}>Model name</Header>,
    cell: (info) => {
      const row = info.row.original;
      return (
        <Cell info={info}>
          <div className="flex items-center gap-x-2">
            <ModelIcon
              iconUrl={row.iconPath}
              modelName={row.displayName}
              provider={(row.provider ?? "") as ModelProvider}
            />
            <span>{row.displayName}</span>
          </div>
        </Cell>
      );
    },
    minSize: 280,
  }),
  columnHelper.accessor((r) => r.tokensUsed, {
    id: "tokenUsage",
    header: (info) => <Header info={info}>Monthly token usage</Header>,
    cell: (info) => {
      const row = info.row.original;
      return (
        <Cell info={info}>
          {formatCompactTokens(row.tokensUsed)} / {formatLimit(row.tokenQuota)}
        </Cell>
      );
    },
    size: 240,
    minSize: 240,
    maxSize: 240,
  }),
  columnHelper.accessor("tokensPerMinuteLimit", {
    header: (info) => (
      <Header info={info}>
        <span className="inline-flex items-center gap-x-1">
          Max tokens/min
          <InfoTooltip title={TOKENS_PER_MIN_TOOLTIP} />
        </span>
      </Header>
    ),
    cell: (info) => <Cell info={info}>{formatLimit(info.getValue())}</Cell>,
    size: 200,
    minSize: 200,
    maxSize: 200,
  }),
  columnHelper.accessor("requestsPerMinuteLimit", {
    header: (info) => (
      <Header info={info}>
        <span className="inline-flex items-center gap-x-1">
          Max requests/min
          <InfoTooltip title={REQUESTS_PER_MIN_TOOLTIP} />
        </span>
      </Header>
    ),
    cell: (info) => <Cell info={info}>{formatCount(info.getValue())}</Cell>,
    size: 220,
    minSize: 220,
    maxSize: 220,
  }),
];

const AiUsageLimitsSkeleton: React.FC = () => (
  <Card className="flex h-full flex-col pt-1" variant="inverted" fullWidth>
    <Card.Content className="min-h-0 flex-1">
      <Table columns={columns} data={[]} isLoading loadingRowsCount={8} />
    </Card.Content>
  </Card>
);

type ContentProps = {
  models: BasicModelCard[];
  quota: AiNodeQuota;
};

const AiUsageLimitsContent: React.FC<ContentProps> = ({ models, quota }) => {
  const rows = useMemo(() => buildRows(models, quota), [models, quota]);

  return (
    <Card className="flex h-full flex-col pt-1" variant="inverted" fullWidth>
      <Card.Content className="min-h-0 flex-1">
        <Table columns={columns} data={rows} isLoading={false} />
      </Card.Content>
    </Card>
  );
};

type Props = {
  workspaceId: string;
};

export const AiUsageLimitsPage: React.FC<Props> = ({ workspaceId }) => {
  const workspace = useWorkspace(workspaceId);
  const baseUrl = workspace.data?.base_url;
  const availableModels = useAvailableAiModels(baseUrl);
  const quotas = useAiNodeQuotas(baseUrl);

  return (
    <DashboardContent
      Header={
        <SubHeader
          title={
            <div className="flex items-center gap-x-1">
              AI usage limits
              <InfoTooltip title={PAGE_TOOLTIP} />
            </div>
          }
        />
      }
    >
      <LoadingView
        queryResult={[availableModels, quotas]}
        renderUpdated={([models, quota]: [BasicModelCard[], AiNodeQuota]) => (
          <AiUsageLimitsContent models={models} quota={quota} />
        )}
        renderUpdating={() => <AiUsageLimitsSkeleton />}
      />
    </DashboardContent>
  );
};
